import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { usePeriod } from '@/lib/period';
import { PeriodControl } from '@/features/shared/PeriodControl';
import { TenantsCountCard } from './TenantsCountCard';
import { LabelsPerDayLargeChart } from './LabelsPerDayLargeChart';
import { ProductsCountCard } from './ProductsCountCard';
import { TenantsByCountryBarChart } from './TenantsByCountryBarChart';
import { TopLabelsAccountsTable } from './TopLabelsAccountsTable';
import { useUsageBasic } from './useUsageBasic';

/**
 * Tela `/dashboard/usage-basic`: etiquetas por dia (série global), tenants
 * por país, totais de tenants e produtos, e o top 10 de contas por etiquetas.
 *
 * Origem: sem issue — commit f6969de1f, 2026-05-04 (Sprint 4.5, ver
 * `docs/sprint-4.5-usage-basic.md`).
 *
 * Contrato: sem props; período pelo `usePeriod` (os totais de tenants e de
 * produtos ignoram o período). Estados LOADING/ERROR/READY vêm de
 * `useUsageBasic`.
 *
 * Usado por: `routes/router.tsx` (rota `dashboard/usage-basic`, só
 * super-admin).
 *
 * Arquitetura: só apresentação, sem escrita; leitura no `useUsageBasic`.
 */
export function UsageBasicDashboardPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { period, setPeriod } = usePeriod();
  const state = useUsageBasic(period);

  return (
    <ContentLayout
      header={<Header variant="h1" description={t('usageBasic.subtitle')}>{t('usageBasic.title')}</Header>}
    >
      <SpaceBetween size="l">
        <PeriodControl value={period} onChange={setPeriod} />

        {state.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {state.kind === 'ERROR' && <Alert type="error">{state.error}</Alert>}
        {state.kind === 'READY' && (
          <SpaceBetween size="l">
            <LabelsPerDayLargeChart series={state.metrics.series} />
            <ColumnLayout columns={2}>
              <TenantsByCountryBarChart data={state.metrics.tenantsByCountry} />
              <ColumnLayout columns={2}>
                <TenantsCountCard total={state.metrics.tenantsTotal} />
                <ProductsCountCard total={state.metrics.productsTotal} />
              </ColumnLayout>
            </ColumnLayout>
            <TopLabelsAccountsTable rows={state.metrics.topLabelsAccounts} />
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}
